import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { visitsApiRef, type VisitInput } from '@backstage/plugin-home';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ENTITY_PATH = /^\/catalog\/(?<namespace>[^/]+)\/(?<kind>[^/]+)\/(?<name>[^/]+)/;

/*
 * Builds the visit to record for a route, or `undefined` for non-entity routes.
 *
 * The pathname is normalized to the canonical entity route
 * (`/catalog/:namespace/:kind/:name`), dropping any sub-tab segment. The visits
 * storage dedupes by pathname, so recording each sub-tab's pathname would store
 * one record per tab and surface the same entity multiple times in Recently
 * viewed. Normalizing collapses them onto one record (and lands the row link on
 * the entity overview).
 */
export function toEntityVisit(pathname: string): VisitInput | undefined {
  const groups = ENTITY_PATH.exec(pathname)?.groups;
  if (!groups) {
    return undefined;
  }
  return {
    name: groups.name,
    pathname: `/catalog/${groups.namespace}/${groups.kind}/${groups.name}`,
    entityRef: stringifyEntityRef({
      namespace: groups.namespace,
      kind: groups.kind,
      name: groups.name,
    }),
  };
}

/*
 * Records entity page views for the home page's Recently viewed panel.
 *
 * This stands in for the home plugin's own `VisitListener`, which does not work
 * in this app: it saves from inside a `requestAnimationFrame` whose effect
 * depends on two callbacks it recreates every render, so each re-render cancels
 * the pending frame and the save never runs. Its default name resolver is also
 * broken here — it reads `global.document`, and `global` is undefined in the
 * app bundle.
 *
 * Only entity routes are recorded; `canSave` on the visits API applies the same
 * rule on the way into storage.
 */
export function VisitRecorder() {
  const visitsApi = useApi(visitsApiRef);
  const { pathname } = useLocation();

  useEffect(() => {
    const visit = toEntityVisit(pathname);
    if (!visit) {
      return;
    }
    visitsApi.save({ visit }).catch(() => {});
  }, [visitsApi, pathname]);

  return null;
}
